import Phaser from 'phaser';
import { COLORS } from './theme';

export interface CheckButtonOptions {
  size?: number;
  fill?: number;
  stroke?: number;
  strokeWidth?: number;
  onClick?: () => void;
}

// Bold green check mark drawn as a thick-stroked two-segment polyline. Two
// passes: outer dark outline, inner light fill. Filled circles at every
// vertex give rounded caps + a rounded bend, and ensure the dark border
// wraps fully around both ends.
export class CheckButton extends Phaser.GameObjects.Container {
  private readonly baseScale = 1;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: CheckButtonOptions = {}) {
    super(scene, x, y);
    scene.add.existing(this);

    const size = opts.size ?? 140;
    const fill = opts.fill ?? COLORS.accentGreen;
    const stroke = opts.stroke ?? COLORS.accentGreenDeep;
    const outlineExtra = opts.strokeWidth ?? 6;

    const s = size / 2;
    const innerThickness = size * 0.22;
    const outerThickness = innerThickness + outlineExtra * 2;

    const leftEnd  = { x: -s * 0.78, y:  s * 0.05 };
    const bend     = { x: -s * 0.15, y:  s * 0.55 };
    const rightEnd = { x:  s * 0.78, y: -s * 0.55 };
    const points = [leftEnd, bend, rightEnd];

    const g = scene.add.graphics();
    this.add(g);

    // Pass 1: outer dark outline (strokes + endpoint/bend caps).
    g.lineStyle(outerThickness, stroke, 1);
    g.beginPath();
    g.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) g.lineTo(points[i].x, points[i].y);
    g.strokePath();
    g.fillStyle(stroke, 1);
    for (const p of points) g.fillCircle(p.x, p.y, outerThickness / 2);

    // Pass 2: inner green fill (strokes + endpoint/bend caps).
    g.lineStyle(innerThickness, fill, 1);
    g.beginPath();
    g.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) g.lineTo(points[i].x, points[i].y);
    g.strokePath();
    g.fillStyle(fill, 1);
    for (const p of points) g.fillCircle(p.x, p.y, innerThickness / 2);

    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const margin = outerThickness / 2;
    const minX = Math.min(...xs) - margin;
    const maxX = Math.max(...xs) + margin;
    const minY = Math.min(...ys) - margin;
    const maxY = Math.max(...ys) + margin;
    const hitW = maxX - minX;
    const hitH = maxY - minY;

    this.setInteractive(new Phaser.Geom.Rectangle(minX, minY, hitW, hitH), Phaser.Geom.Rectangle.Contains);
    this.input!.cursor = 'pointer';
    if ((scene as unknown as { debugHit?: boolean }).debugHit) {
      const dbg = scene.add.graphics();
      this.add(dbg);
      dbg.lineStyle(2, 0x00ff00, 1);
      dbg.strokeRect(minX, minY, hitW, hitH);
      scene.input.enableDebug(this, 0xffff00);
    }

    this.on('pointerover', () => this.setScale(this.baseScale * 1.08));
    this.on('pointerout',  () => this.setScale(this.baseScale));
    this.on('pointerdown', () => this.setScale(this.baseScale * 0.94));
    this.on('pointerup',   () => {
      this.setScale(this.baseScale * 1.08);
      opts.onClick?.();
    });
  }
}
